import axios, { AxiosError } from 'axios'

export type Pagination = { page: number; perPage: number; total: number }

// Error thrown for API error envelopes; keeps code/details (e.g. OUT_OF_STOCK items)
export class ApiError extends Error {
  code?: string
  details: Record<string, unknown>
  status?: number

  constructor(message: string, code?: string, details: Record<string, unknown> = {}, status?: number) {
    super(message)
    this.code = code
    this.details = details
    this.status = status
  }
}

type ErrorBody = { error?: { code?: string; message?: string; details?: Record<string, unknown> } }

// Axios instance for /api/v1/* modules: bearer token + error envelope -> ApiError
export const createApiClient = (path: string) => {
  const api = axios.create({ baseURL: `${import.meta.env.VITE_APP_BASE_API_URL}${path}` })

  api.interceptors.request.use((config) => {
    const token = localStorage.getItem('userToken')
    if (token) config.headers.Authorization = `Bearer ${token}`
    return config
  })

  api.interceptors.response.use(
    (response) => response,
    (error: AxiosError<ErrorBody>) => {
      // revoked / expired session (account deleted, signed out everywhere): back to login
      if (error.response?.status === 401 && localStorage.getItem('userToken')) {
        ;['userToken', 'userRole', 'userInfo', 'userPermissions'].forEach((key) => localStorage.removeItem(key))
        const here = window.location.pathname + window.location.search
        window.location.assign(`/auth/login?redirect=${encodeURIComponent(here)}`)
      }
      const body = error.response?.data?.error
      return Promise.reject(
        new ApiError(body?.message || error.message, body?.code, body?.details || {}, error.response?.status),
      )
    },
  )

  return api
}

// Drops empty values so they are not sent as query params
export const cleanParams = (params: object = {}) =>
  Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ''))
